import { CurrencyPipe, JsonPipe, NgFor, NgIf, PercentPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, model, OnInit, inject, OnChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ActiveTaxList } from 'src/app/shared/entities/ActiveTaxList';
import { RevenueTypes } from 'src/app/shared/entities/RevenueTypes';
import { ILotInvoiceMiSettingsResponse, IMiNewInvoiceLine } from 'src/app/shared/interfaces';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmRemoveTaxComponent } from 'src/app/quick-search/dialog/confirm-remove-tax.component';
import { ConfirmAddTaxComponent } from 'src/app/quick-search/dialog/confirm-add-tax.component';
import { NotifyTaxOnlyComponent } from 'src/app/quick-search/dialog/notify-tax-only.component';
import { NotifyNoTaxOnlyComponent } from 'src/app/quick-search/dialog/notify-no-tax-only.component';
import { DataService } from 'src/app/core/services/data/data.service';
import { firstValueFrom } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ContentObserver } from '@angular/cdk/observers';

@Component({
  selector: 'app-mi-new-invoice-line',
  standalone: true,
  imports: [
    MatCardModule,
    MatRadioModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    CurrencyPipe,
    PercentPipe,
    NgIf,
    JsonPipe,
  ],
  templateUrl: './mi-new-invoice-line.component.html',
  styleUrl: './mi-new-invoice-line.component.scss',
})
export class MiNewInvoiceLineComponent implements OnInit, OnChanges {
  isTaxCalculated: boolean = false;
  allRevenueTypes: RevenueTypes[] = [];
  filteredRevenueTypes: RevenueTypes[] = [];
  activeTaxListForLot: ActiveTaxList[] = [];
  isNetCalcMode: boolean = false;
  netGrossLabel: string = 'Gross';
  selectedRevenueCode: string | undefined = undefined;
  selectedRevenueType: RevenueTypes | undefined = undefined;
  currentRateCodeIsNonTaxableOnly: boolean = false;
  //isTaxable: boolean = false; not used
  //applyTax: boolean = true;
  miInvoiceSpecificSettings: ILotInvoiceMiSettingsResponse | undefined = undefined;
  userTaxEnabledDisabled: boolean = false; //if user disables tax
  applyCheckboxTaxFactor: boolean = true;
  unitTaxAmount: number = 0;
  lotParkingRate: number = 0;

  //calc result vars
  displayRate: number = 0;
  displayNetAmount: number = 0;
  displayGrossAmount: number = 0;
  displayTaxRate: number = 0;
  displayTaxAmount: number = 0;

  invoiceLineForm = new FormGroup({
    revenueCode: new FormControl('', [Validators.required]),
    isTaxable: new FormControl({ value: true, disabled: true }, []),
    rate: new FormControl('0', [
      Validators.required,
      Validators.min(0.01),
      Validators.pattern('^[0-9]*\\.?[0-9]{0,2}$'),
    ]),
    quantity: new FormControl('0', [Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]),
    description: new FormControl('', [Validators.required]),
    sequenceStart: new FormControl('', [Validators.pattern('^[0-9]*$')]),
    sequenceEnd: new FormControl('', [Validators.pattern('^[0-9]*$')]),
  });

  taxableRevenueShouldBeApplied: boolean = true;
  typeRevenueCalculation: string = 'NET';
  addRevenueIsTaxableCheckBoxGross: boolean = true;
  addRevenueIsTaxableCheckBoxNet: boolean = true;
  isTaxableBeforeClick: boolean = false;

  // SERVICES
  _dataService = inject(DataService);
  _appGeneralState = inject(AppGeneralStateService);

  @Input() lotNumber: string;

  @Input() set revTypes(value: RevenueTypes[]) {
    this.allRevenueTypes = value;
    this.filteredRevenueTypes = this.allRevenueTypes;
  }

  @Input() set taxRateList(value: ActiveTaxList[]) {
    this.activeTaxListForLot = value;
    if (this.activeTaxListForLot.length > 0) {
      this.displayTaxRate = this.currentTaxRate();
    }
  }

  @Input() set invoiceSettings(value: ILotInvoiceMiSettingsResponse) {
    this.miInvoiceSpecificSettings = value;
  }

  @Output() newInvoiceLineEvent = new EventEmitter<IMiNewInvoiceLine>();

  //Services
  #_matDialog = inject(MatDialog);

  constructor() {}

  ngOnInit(): void {
    this.invoiceLineForm.get('revenueCode')!.valueChanges.subscribe(() => this.onChangeRevenueType());
    this.invoiceLineForm.valueChanges.subscribe(() => this.onCalculateRateData());
  }

  ngOnChanges(): void {
    if (this.miInvoiceSpecificSettings) this.onFilterRevenueTypesForLot(this.miInvoiceSpecificSettings);
  }

  onToggleNetGrossMode(): void {
    this.isNetCalcMode = !this.isNetCalcMode;

    if (this.isNetCalcMode) this.netGrossLabel = 'Net';
    else this.netGrossLabel = 'Gross';

    this.onCalculateRateData();
  }

  onRateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let sanitizedValue = input.value.replace(/[^0-9.]/g, '');

    const firstDecimalIndex = sanitizedValue.indexOf('.');
    if (firstDecimalIndex !== -1) {
      const wholePart = sanitizedValue.substring(0, firstDecimalIndex + 1);
      const decimalPart = sanitizedValue
        .substring(firstDecimalIndex + 1)
        .replace(/\./g, '')
        .slice(0, 2);
      sanitizedValue = `${wholePart}${decimalPart}`;
    }

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
      this.invoiceLineForm.get('rate')?.setValue(sanitizedValue);
    }
  }

  onQuantityInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = input.value.replace(/\D/g, '');

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
      this.invoiceLineForm.get('quantity')?.setValue(sanitizedValue);
    }
  }

  onResetInvoiceLine(): void {
    this.displayRate = 0;
    this.displayNetAmount = 0;
    this.displayGrossAmount = 0;
    // this.displayTaxRate = this.activeTaxListForLot.length > 0 ? this.activeTaxListForLot[0].taxAmount / 100 : 0;
    this.displayTaxRate = this.currentTaxRate();
    this.displayTaxAmount = 0;

    this.invoiceLineForm.reset({
      revenueCode: '',
      isTaxable: true,
      rate: '0',
      quantity: '0',
      description: '',
      sequenceStart: '',
      sequenceEnd: '',
    });

    this.invoiceLineForm.get('isTaxable')?.disable({ emitEvent: false });
    this.invoiceLineForm.markAsPristine();
    this.invoiceLineForm.markAsUntouched();
    this.invoiceLineForm.updateValueAndValidity({ emitEvent: false });
  }

  onChangeRevenueType(): void {
    const revCode: string = this.invoiceLineForm.get('revenueCode')?.value ?? '';
    const selectedType: RevenueTypes | undefined = this.filteredRevenueTypes.find((rt) => rt.code === revCode);
    this.setTaxAppliedCheckbox(selectedType);
    this.onCalculateRateData();
  }

  confirmChangeTaxesFromAddedRevenue(): void {
    const changeTaxableStatus: boolean = this.checkTaxableAttributesOfRevenueType();

    if (false == changeTaxableStatus) {
      return;
    }
    if (this.taxableRevenueShouldBeApplied === true) {
      const dialogRef = this.#_matDialog.open(ConfirmRemoveTaxComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });
      //return when dialog closed and process if proceed or cancel
      dialogRef.afterClosed().subscribe((result) => {
        if (result == true) {
          if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = false;
          else this.addRevenueIsTaxableCheckBoxNet = false;
          this.taxableRevenueShouldBeApplied = false;
          this.userTaxEnabledDisabled = !this.taxableRevenueShouldBeApplied;
        } else {
          if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = true;
          else this.addRevenueIsTaxableCheckBoxNet = true;
          this.taxableRevenueShouldBeApplied = true;
          this.userTaxEnabledDisabled = !this.taxableRevenueShouldBeApplied;
        }
        this.onCalculateRateData();
      });
    } else {
      const dialogTurnTaxOnRef = this.#_matDialog.open(ConfirmAddTaxComponent, {});
      dialogTurnTaxOnRef.afterClosed().subscribe((result) => {
        if (result == true) {
          if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = true;
          else this.addRevenueIsTaxableCheckBoxNet = true;
          this.taxableRevenueShouldBeApplied = true;
          this.userTaxEnabledDisabled = !this.taxableRevenueShouldBeApplied;
        } else {
          if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = false;
          else this.addRevenueIsTaxableCheckBoxNet = false;
          this.taxableRevenueShouldBeApplied = false;
          this.userTaxEnabledDisabled = !this.taxableRevenueShouldBeApplied;
        }

        this.onCalculateRateData();
      });
    }
  }

  onValidateSequenceNumbers(): void {
    const lengthSequenceStart: number = +(this.invoiceLineForm.get('sequenceStart')?.value ?? 0);
    const lengthSequenceEnd: number = +(this.invoiceLineForm.get('sequenceEnd')?.value ?? 0);
    const quantity: number = +(this.invoiceLineForm.get('quantity')?.value ?? 0);

    this.invoiceLineForm.clearValidators();

    //Err length on sequence end
    if (lengthSequenceStart > 0 && lengthSequenceEnd == 0) {
      var startQuantity: number = +(this.invoiceLineForm.get('sequenceStart')?.value ?? 0);
      this.invoiceLineForm.controls.sequenceStart.setErrors({ sequenceStartRequired: false });
      this.invoiceLineForm.controls.sequenceEnd.setErrors({ sequenceEndRequired: true });
      const newQuantity: number = startQuantity + quantity - 1;

      this.invoiceLineForm.patchValue({
        sequenceEnd: newQuantity.toString(),
      });

      this.invoiceLineForm.get('sequenceStart')?.clearValidators();
      this.invoiceLineForm.get('sequenceStart')?.updateValueAndValidity();
    }

    // Err length on sequence start
    if (lengthSequenceStart == 0 && lengthSequenceEnd > 0) {
      this.invoiceLineForm.controls.sequenceEnd.setErrors({ sequenceEndRequired: false });
      this.invoiceLineForm.controls.sequenceStart.setErrors({ sequenceStartRequired: true });
    }

    if (lengthSequenceEnd > 0 && lengthSequenceStart > 0) {
      var startValue: number = +(this.invoiceLineForm.get('sequenceStart')?.value ?? 0);
      var endValue: number = +(this.invoiceLineForm.get('sequenceEnd')?.value ?? 0);

      if (endValue < startValue) {
        this.invoiceLineForm.controls.sequenceEnd.setErrors({ sequenceEndLessThanStart: true });
      } else {
        this.invoiceLineForm.get('sequenceStart')?.clearValidators();
        this.invoiceLineForm.get('sequenceStart')?.updateValueAndValidity();
        this.invoiceLineForm.get('sequenceEnd')?.clearValidators();
        this.invoiceLineForm.get('sequenceEnd')?.updateValueAndValidity();
      }
    }

    if (lengthSequenceStart == 0 && lengthSequenceEnd == 0) {
      this.invoiceLineForm.get('sequenceStart')?.clearValidators();
      this.invoiceLineForm.get('sequenceStart')?.updateValueAndValidity();
      this.invoiceLineForm.get('sequenceEnd')?.clearValidators();
      this.invoiceLineForm.get('sequenceEnd')?.updateValueAndValidity();
    }

    //    this.invoiceLineForm.updateValueAndValidity();

    // this.invoiceLineForm.controls.sequenceEnd.setErrors({ sequenceStartRequired: true });
    // this.invoiceLineForm.get('sequenceStart')!.setErrors({ sequenceStartRequired: true });
  }

  checkTaxableAttributesOfRevenueType(): boolean {
    let revenueTypeCode: string = this.invoiceLineForm.get('revenueCode')?.value ?? '';

    let taxableStatus: number = 0; //0 - taxable/non-taxable, 1-taxable only, 2-non-taxable only, Set to 2 by default

    let ii: number = 0;

    for (ii = 0; ii < this.filteredRevenueTypes.length; ii++) {
      if (revenueTypeCode == this.filteredRevenueTypes[ii].code) {
        if (this.filteredRevenueTypes[ii].isTaxAndNoTax) {
          taxableStatus = 0;
        }

        if (this.filteredRevenueTypes[ii].isTaxableOnly) {
          taxableStatus = 1;
        }

        if (this.filteredRevenueTypes[ii].isNonTaxableOnly) {
          taxableStatus = 2;
        }
      }
    }

    if (taxableStatus == 1) {
      const dialogRef = this.#_matDialog.open(NotifyTaxOnlyComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = true;
        else this.addRevenueIsTaxableCheckBoxNet = true;

        this.taxableRevenueShouldBeApplied = true;
        this.setTaxAppliedToChecked();
      });
    }

    if (taxableStatus == 2) {
      const dialogRef = this.#_matDialog.open(NotifyNoTaxOnlyComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = false;
        else this.addRevenueIsTaxableCheckBoxNet = false;

        this.taxableRevenueShouldBeApplied = false;
      });
    }

    return taxableStatus == 0;
  }

  setTaxAppliedToChecked(): void {
    this.invoiceLineForm.get('isTaxable')!.setValue(true);
  }

  setTaxAppliedToUnchecked(): void {
    this.invoiceLineForm.get('isTaxable')!.setValue(false);
  }

  onSubmitNewInvoiceLine(): void {
    let miFormData: IMiNewInvoiceLine = {} as IMiNewInvoiceLine;

    const revCode: string = this.invoiceLineForm.get('revenueCode')?.value ?? '';
    const revText: string = this.allRevenueTypes.find((type) => type.code == revCode)?.description ?? '';

    miFormData.description = this.invoiceLineForm.get('description')?.value ?? '';
    miFormData.quantity = +(this.invoiceLineForm.get('quantity')?.value ?? 0);
    miFormData.revenueCode = revCode;
    miFormData.revenueType = revText;
    miFormData.sequenceEnd = this.invoiceLineForm.get('sequenceEnd')?.value ?? '';
    miFormData.sequenceStart = this.invoiceLineForm.get('sequenceStart')?.value ?? '';
    //miFormData.rate = +this.invoiceLineForm.get('rate').value;
    miFormData.taxAmount = +this.displayTaxAmount;
    miFormData.unitTax = this.currentTaxRate();
    miFormData.unitRate = +(this.invoiceLineForm.get('rate')?.value ?? 0);
    miFormData.netAmount = +this.displayNetAmount;
    miFormData.ratePerUnit = Math.round((miFormData.netAmount / miFormData.quantity + Number.EPSILON) * 100) / 100;
    miFormData.rate = miFormData.ratePerUnit;
    miFormData.unitTaxAmount = +this.unitTaxAmount;
    miFormData.lotParkingRate = miFormData.unitRate;
    miFormData.taxApplied = this.invoiceLineForm.get('isTaxable')?.value ?? false;

    if (this.isNetCalcMode) {
      miFormData.totalAmount = miFormData.quantity * miFormData.rate + miFormData.taxAmount;
    } else {
      miFormData.totalAmount = miFormData.quantity * miFormData.rate;
    }

    console.log('miformdata', miFormData);

    this.newInvoiceLineEvent.emit(miFormData);
    this.onResetInvoiceLine();
  }

  onCalculateRateData(): void {
    const rateAmount: number = +(this.invoiceLineForm.get('rate')?.value ?? 0);
    const quantity: number = +(this.invoiceLineForm.get('quantity')?.value ?? 0);

    this.displayRate = rateAmount;
    this.displayTaxRate = this.currentTaxRate();

    if (this.isNetCalcMode) {
      if (this.userTaxEnabledDisabled) {
        this.displayNetAmount = rateAmount * quantity;
        this.displayTaxAmount = this.currentTaxRate();
        this.displayGrossAmount = this.displayNetAmount + this.displayTaxAmount;
      } else {
        this.displayNetAmount = rateAmount * quantity;
        this.displayTaxAmount = this.displayNetAmount * this.currentTaxRate();
        this.displayGrossAmount = this.displayNetAmount + this.displayTaxAmount;
      }
    } else {
      // Calculate from GROSS Amount
      if (this.userTaxEnabledDisabled) {
        this.displayGrossAmount = rateAmount * quantity;
        this.displayNetAmount = 0;
        this.displayTaxAmount = this.displayGrossAmount - this.displayNetAmount;
      } else {
        // this.displayRate = Math.round((rateAmount / (1 + this.currentTaxRate()) + Number.EPSILON) * 100) / 100; // calculate the unit net rate so net + tax = gross
        // this.unitTaxAmount = Math.round((this.displayRate * this.currentTaxRate() + Number.EPSILON) * 100) / 100;
        // this.displayNetAmount = this.displayRate * quantity;
        // this.displayTaxAmount = this.unitTaxAmount * quantity;
        // this.displayGrossAmount = this.displayNetAmount + this.displayTaxAmount;
        // console.log('Full Net Rate', rateAmount / (1 + this.currentTaxRate()));
        // console.log('Unit Tax Amount', this.displayRate * this.currentTaxRate());

        // this.displayRate = rateAmount / (1 + this.currentTaxRate()); // calculate the unit net rate so net + tax = gross
        // this.unitTaxAmount = this.displayRate * this.currentTaxRate();
        // this.displayNetAmount = this.displayRate * quantity;
        // this.displayTaxAmount = this.unitTaxAmount * quantity;
        // this.displayGrossAmount = this.displayNetAmount + this.displayTaxAmount;

        // Gross Rate Calculation Overview
        // 1) Start with the Gross Rate for a given revenue code and rate number.
        // 2) Calculate the unit net rate by dividing the gross rate by (1 + tax rate).
        // 3) Calculate the unit tax amount by multiplying the unit net rate by the tax rate.
        // 4) Calculate the net subtotal by multiplying the unit net rate by the quantity.
        // 5) Calculate the tax subtotal by multiplying the unit tax amount by the quantity.
        // 6) Calculate the line total by adding the net subtotal and tax subtotal.
        // Note: critical that the end net unit rate, unit tax amount are rounded so
        //        they can be used in future calcs to get back to the desired Gross Amount (rateAmount * quantity)

        // t = temporary calc vars below
        const tQuantity: number = quantity;
        const tunitRate: number = rateAmount;
        const tnetAmountUnrounded: number = tunitRate / (1 + this.currentTaxRate());
        const tnetAmountRounded: number = Math.round((tnetAmountUnrounded + Number.EPSILON) * 100) / 100;
        const ttaxAmountUnRounded: number = tunitRate - tnetAmountUnrounded;
        const ttaxAmountRounded: number = Math.round((ttaxAmountUnRounded + Number.EPSILON) * 100) / 100;
        const tNetSubTotal: number = Math.round((tnetAmountRounded * tQuantity + Number.EPSILON) * 100) / 100;
        const tTaxSubTotal: number = Math.round((ttaxAmountRounded * tQuantity + Number.EPSILON) * 100) / 100;
        const tLineTotal: number = tNetSubTotal + tTaxSubTotal;

        // Assign to display variables in their rounded end units
        this.displayRate = tnetAmountRounded;
        this.unitTaxAmount = ttaxAmountRounded;
        this.displayNetAmount = tNetSubTotal;
        this.displayTaxAmount = tTaxSubTotal;
        this.displayGrossAmount = tLineTotal;
        this.lotParkingRate = rateAmount;
      }
    }
  }

  isRateTaxable(rateCode: string): boolean {
    return this.filteredRevenueTypes.some((rev) => rev.code === rateCode && !rev.isFinal);
  }

  setCheckboxDisabledStatus(disabled: boolean): void {
    const checkbox = this.invoiceLineForm.get('isTaxable');
    if (disabled) {
      checkbox?.disable();
    } else {
      checkbox?.enable();
    }
  }

  calculateAmountsFromNet(taxRate: number, netAmount: number): number {
    let taxAmount: number = 0;

    if (taxRate > 0) {
      taxAmount = taxRate * netAmount;
    }
    return taxAmount;
  }

  onEvalTaxApplied(event: MouseEvent): void {
    this.evalHandleTaxAppliedCheckboxClick(event);
    return;

    const revCode: string = this.invoiceLineForm.get('revenueCode')?.value ?? '';
    const revenueType = this.filteredRevenueTypes.find((rt) => rt.code === revCode);
    event.preventDefault();

    const currentTaxAppliedValue = this.invoiceLineForm.get('isTaxable')?.value;

    if (revenueType?.isEnforceable) {
      const dialogTurnTaxOnRef = this.#_matDialog.open(ConfirmAddTaxComponent, {});
      dialogTurnTaxOnRef.afterClosed().subscribe((result) => {
        if (result == true) {
          this.invoiceLineForm.get('isTaxable')!.setValue(!currentTaxAppliedValue);
        } else {
        }
      });
    } else {
      const dialogRef = this.#_matDialog.open(ConfirmRemoveTaxComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });
      //return when dialog closed and process if proceed or cancel
      dialogRef.afterClosed().subscribe((result) => {
        if (result == true) {
          this.invoiceLineForm.get('isTaxable')!.setValue(!currentTaxAppliedValue);
        } else {
        }
      });
    }
  }

  /* =========================================================================
THIS IS GOOD CODE BELOW
  * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  onTaxableMouseDown(): void {
    this.isTaxableBeforeClick = !!this.invoiceLineForm.get('isTaxable')?.value;
  }

  evalHandleTaxAppliedCheckboxClick(event: MouseEvent): void {
    const revCode: string = this.invoiceLineForm.get('revenueCode')?.value ?? '';
    const revenueType = this.filteredRevenueTypes.find((rt) => rt.code === revCode);
    event.preventDefault();

    const isCurrentlyChecked = this.isTaxableBeforeClick;
    this.invoiceLineForm.get('isTaxable')?.setValue(isCurrentlyChecked, { emitEvent: false });

    // Can this revenue type be changed
    if (revenueType?.isEnforceable) {
      // Cannot change the tax applied checkbox
      // Notify user they cannot change this and reset to correct value based on revenue type
      this.notifyUserCheckboxCannotBeChanged(isCurrentlyChecked);
    } // We can change tax on this path
    else {
      if (isCurrentlyChecked) {
        // User is trying to uncheck (remove tax) — confirm first
        const dialogRef = this.#_matDialog.open(ConfirmRemoveTaxComponent, {
          width: '900px',
          height: '270px',
          position: { top: '75px' },
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result == true) {
            this.invoiceLineForm.get('isTaxable')!.setValue(false);
            this.applyCheckboxTaxFactor = false;
            this.onCalculateRateData();
          }
        });
      } else {
        // User is trying to check (add tax) — confirm first
        const dialogRef = this.#_matDialog.open(ConfirmAddTaxComponent, {
          width: '900px',
          height: '270px',
          position: { top: '75px' },
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result == true) {
            this.invoiceLineForm.get('isTaxable')!.setValue(true);
            this.applyCheckboxTaxFactor = true;
            this.onCalculateRateData();
          }
        });
      }
    }
  }

  notifyUserConfirmAddTax(): void {}

  notifyUserRemoveTax(): void {}

  notifyUserCheckboxCannotBeChanged(isCurrentlyChecked: boolean): void {
    // if checked, then show the notice this is taxable and cannot be changed
    if (isCurrentlyChecked) {
      const dialogRef = this.#_matDialog.open(NotifyTaxOnlyComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });

      dialogRef.afterClosed().subscribe((result) => {});
    } // if unchecked, then show the notice this is non-taxable and cannot be changed
    else {
      const dialogRef = this.#_matDialog.open(NotifyNoTaxOnlyComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });

      dialogRef.afterClosed().subscribe((result) => {});
    }
  }

  onRevenueTypeChange(): void {
    const revCode: string | null = this.invoiceLineForm.get('revenueCode')!.value;
    const selectedRevenueType: RevenueTypes | undefined = this.allRevenueTypes.find((rev) => rev.code === revCode);
    this.selectedRevenueCode = revCode ?? undefined;
    this.selectedRevenueType = selectedRevenueType;
    console.log('Selected Revenue Code:', this.selectedRevenueCode);
    console.log('Selected Revenue Type:', this.selectedRevenueType);
  }

  currentTaxRate(): number {
    let taxRate: number = 0;

    // 20260824 :: Ken Moss
    // This is a temporary patch per Josh Izigzon to handle an error on legacy APN application admin feature.
    // If the selected revenue code is 'PAYRM', return 0 tax rate (no tax on payroll).  
    if(this.selectedRevenueCode?.toUpperCase() === 'PAYRM') {
        return 0;
    }

    if (this.applyCheckboxTaxFactor == false) {
      return 0;
    }

    if (this.currentRateCodeIsNonTaxableOnly) {
      return 0;
    }

    // if number of tax rates == 1
    if (this.activeTaxListForLot.length == 1) {
      taxRate = this.activeTaxListForLot[0].taxAmount;
    } else if (this.activeTaxListForLot.length > 1) {
      taxRate = this.activeTaxListForLot.reduce((sum, tax) => sum + tax.taxAmount, 0);
    }
    return taxRate;
  }

  currentDisplayTaxRate(): string {
    if (this.currentRateCodeIsNonTaxableOnly) {
      return '0.00%';
    }

    const taxRate: number = Math.round(this.currentTaxRate() * 10000) / 10000;
    const taxRateDisplay: string = (taxRate * 100).toFixed(2) + '%';

    return taxRateDisplay;
  }

  onFilterRevenueTypesForLot(miLotSettings: ILotInvoiceMiSettingsResponse): void {
    if (miLotSettings.allowMonthlyParkerRevenueCodes == false) {
      this.filteredRevenueTypes = this.filteredRevenueTypes.filter((rt) => rt.description.toUpperCase() !== 'MONTHLY');
    }
  }

  setTaxAppliedCheckbox(revenueData: RevenueTypes | undefined): void {
    this.currentRateCodeIsNonTaxableOnly = false;
    this.applyCheckboxTaxFactor = false;

    if (revenueData == undefined) {
      return;
    }

    // This is a checkbox and determines if the user can remove/add tax
    if (revenueData.code == '') {
      this.invoiceLineForm.get('isTaxable')?.disable();
      return;
    }

    // PAYRM revenue code should have no tax applied
    if (revenueData.code?.toUpperCase() === 'PAYRM') {
      this.invoiceLineForm.get('isTaxable')!.setValue(false);
      this.invoiceLineForm.get('isTaxable')?.disable();
      this.applyCheckboxTaxFactor = false;
      return;
    }

    if (revenueData.isNonTaxableOnly) {
      this.invoiceLineForm.get('isTaxable')!.setValue(false);
      this.invoiceLineForm.get('isTaxable')?.disable();
      this.currentRateCodeIsNonTaxableOnly = true;
      this.applyCheckboxTaxFactor = false;
      return;
    }

    // This will be a taxable revenue code
    this.invoiceLineForm.get('isTaxable')!.setValue(true);
    this.invoiceLineForm.get('isTaxable')?.enable();
    this.applyCheckboxTaxFactor = true;
  }
}
